import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe, Loader2, Users } from "lucide-react";

const CONFIGURABLE_ROLES: Array<{ value: string; label: string }> = [
  { value: "artist", label: "Talent / Artist" },
  { value: "sales_rep", label: "Sales Rep" },
  { value: "billing", label: "Billing" },
  { value: "marketing", label: "Marketing" },
];

// Permission keys the Talent module is composed of. Toggling
// talent.module.visible flips the whole group; the sub-features gate the
// individual pages inside it.
const TALENT_PERMISSIONS: Array<{
  key: string;
  label: string;
  description: string;
}> = [
  {
    key: "talent.module.visible",
    label: "Talent module",
    description: "Show the Talent navigation group for this role.",
  },
  {
    key: "talent.feature.callsheets",
    label: "Call Sheets",
    description: "Access the Call Sheets page (create, attach offerings, edit).",
  },
  {
    key: "talent.feature.contacts",
    label: "Contacts",
    description: "Access the talent address book.",
  },
  {
    key: "talent.feature.calendar",
    label: "Calendar",
    description: "Access the per-talent calendar connection hub.",
  },
  {
    key: "myEvents.module.visible",
    label: "My Events",
    description: "Show the talent's own events feed.",
  },
  {
    key: "myProfile.module.visible",
    label: "My Profile",
    description: "Access the talent profile / press-kit page.",
  },
];

export default function TalentPortalSettings() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [role, setRole] = useState("artist");

  const { data: rolePerms, isLoading: rolePermsLoading } = useQuery<{
    role: string;
    permissions: Record<string, boolean>;
  }>({
    queryKey: ["/api/admin/permissions/roles", role],
    queryFn: async () => {
      const r = await fetch(`/api/admin/permissions/roles/${role}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("failed to load role permissions");
      return r.json();
    },
  });

  const { data: bookingSettings } = useQuery<{
    salesRepCanEditCallSheets: boolean;
  }>({
    queryKey: ["/api/admin/booking-settings"],
  });

  const setRolePerm = useMutation({
    mutationFn: async (vars: { permissionKey: string; allowed: boolean }) => {
      const r = await apiRequest(
        "POST",
        `/api/admin/permissions/roles/${role}`,
        vars,
      );
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["/api/admin/permissions/roles", role],
      });
      toast({ title: "Saved", description: "Role permission updated." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to update permission",
        variant: "destructive",
      }),
  });

  const setSalesRepCallSheets = useMutation({
    mutationFn: async (allowed: boolean) => {
      const r = await apiRequest("PATCH", "/api/admin/booking-settings", {
        salesRepCanEditCallSheets: allowed,
      });
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/booking-settings"] });
      toast({ title: "Saved", description: "Call sheet editing updated." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to update setting",
        variant: "destructive",
      }),
  });

  const perms = rolePerms?.permissions || {};

  return (
    <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">Talent Portal</CardTitle>
              <CardDescription>
                Control which roles see the Talent module and its pages. The
                module is off by default for every role except Admin.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm">Configure role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-[200px]" data-testid="select-talent-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONFIGURABLE_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {rolePermsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading permissions…
            </div>
          ) : (
            <div className="space-y-3">
              {TALENT_PERMISSIONS.map((p) => {
                const enabled = perms[p.key] === true;
                const moduleOff =
                  p.key !== "talent.module.visible" &&
                  perms["talent.module.visible"] !== true;
                return (
                  <div
                    key={p.key}
                    className="flex items-start justify-between gap-4 border rounded-lg p-3"
                    data-testid={`row-perm-${p.key}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{p.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.description}
                      </p>
                      {moduleOff && (
                        <Badge variant="outline" className="text-xs mt-1">
                          Hidden until the Talent module is on
                        </Badge>
                      )}
                    </div>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(allowed) =>
                        setRolePerm.mutate({ permissionKey: p.key, allowed })
                      }
                      disabled={setRolePerm.isPending}
                      data-testid={`switch-perm-${p.key}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Call sheet editing</CardTitle>
          <CardDescription>
            Cross-role behavior for call sheets, independent of the Talent
            module toggles above.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-4 border rounded-lg p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Sales reps can edit call sheets</p>
              <p className="text-xs text-muted-foreground">
                When on, sales reps may create and edit call sheets for events
                they work, in addition to talent.
              </p>
            </div>
            <Switch
              checked={!!bookingSettings?.salesRepCanEditCallSheets}
              onCheckedChange={(allowed) =>
                setSalesRepCallSheets.mutate(allowed)
              }
              disabled={setSalesRepCallSheets.isPending}
              data-testid="switch-salesrep-callsheets"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Per-talent financial visibility is configured in the Manage Talent
            edit dialog (Admin Panel → Manage Talent → Edit talent), via the
            &quot;Show booking financials to this artist&quot; toggle, which
            controls whether that artist sees their own booking
            financials/earnings.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
